import { ReactNode, useEffect, useRef, useState } from "react";

type Variant = "danger" | "primary";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: Variant;
  /** Called when the user confirms. If it returns a Promise the dialog
   *  shows a "busy" state and stays open until the promise settles. */
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "primary",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  // Esc closes; lock background scroll; focus the safe action (Cancel) so a
  // stray Enter press doesn't destroy data.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelBtnRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, busy, onCancel]);

  // Reset busy state when the dialog is re-opened fresh.
  useEffect(() => {
    if (!open) setBusy(false);
  }, [open]);

  if (!open) return null;

  async function handleConfirm() {
    try {
      const maybe = onConfirm();
      if (maybe instanceof Promise) {
        setBusy(true);
        await maybe;
      }
    } finally {
      setBusy(false);
    }
  }

  const isDanger = variant === "danger";
  const confirmClass = isDanger
    ? "bg-rose-500/90 hover:bg-rose-500 text-white border border-rose-400/50 shadow-lg shadow-rose-500/20"
    : "bg-accent-500/90 hover:bg-accent-500 text-white border border-accent-400/50";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-150"
      onMouseDown={(e) => {
        // Only dismiss on backdrop click (not bubbled from the card).
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div className="card w-full max-w-md shadow-2xl">
        <div className="p-5 flex items-start gap-4">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              isDanger
                ? "bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/30"
                : "bg-accent-500/15 text-accent-300 ring-1 ring-accent-500/30"
            }`}
          >
            {isDanger ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3
              id="confirm-dialog-title"
              className="text-base font-semibold text-slate-100"
            >
              {title}
            </h3>
            {description && (
              <div className="text-sm text-slate-400 mt-1.5 leading-relaxed">
                {description}
              </div>
            )}
          </div>
        </div>
        <div className="px-5 pb-5 flex justify-end gap-2">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-sm px-4 py-2 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-slate-200 border border-white/10 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={`text-sm px-4 py-2 rounded-md font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
